<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div slot="title">
      <span v-if="type === 'add'">Új vevői visszáru</span>
      <span v-if="type === 'update'">Vevői visszáru módosítása</span>
      <span v-if="type === 'view'">Vevői visszáru megtekintése</span>
      <el-tag
        v-if="customerReturnOrder.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <el-alert
      v-if="customerReturnOrder.status === 'done' && type !== 'view'"
      title="Ez a vevői visszáru már le lett zárva, ezért csak a megjegyzés és számlázandóság módosítható."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <div v-if="type === 'view' && customerReturnOrder.receipt_number">
      <el-link @click="openReceipt"
        type="primary"
        style="margin-bottom: 30px; margin-top: -20px;">
        <i class="el-icon-printer el-icon--right"></i>
        {{ customerReturnOrder.receipt_number }}
      </el-link>
    </div>
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="customerReturnOrder"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-model="customerReturnOrder.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                remote
                :remote-method="searchPartners"
                :loading="partnerLoading"
                placeholder="Írd be a partner nevét a kereséshez..."
                :disabled="customerReturnOrder.status !== 'recorded' || type === 'view'"
                @change="onSupplierChange"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="`#${supplier.id} ${supplier.company.name}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="supplier_address"
              label="Szállító címe"
            >
              <el-select
                v-model="customerReturnOrder.supplier_address"
                v-loading="addressesLoading"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="customerReturnOrder.status !== 'recorded' || addresses.length === 0 || type === 'view'"
              >
                <el-option
                  v-for="address in addresses"
                  :key="address.id + 'address'"
                  :value="address"
                  :label="`${address.full_address}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer"
              label="Átvevő"
            >
              <el-select
                v-model="customerReturnOrder.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="customerReturnOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'customer'"
                  :value="customer"
                  :label="customer.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Raktár

                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="`Ebbe a raktárba érkezik vissza az áru. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                  placement="right"
                >
                  <i class="el-icon-info" />
                </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="customerReturnOrder.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="customerReturnOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="cm_delivery_note_number"
              label="CM szlev száma"
            >
              <el-input
                v-model="customerReturnOrder.cm_delivery_note_number"
                :disabled="type === 'update' && customerReturnOrder.status !== 'recorded'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="customerReturnOrder.currency"
                :disabled="customerReturnOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="completed_at"
              label="Teljesítés dátuma"
            >
              <el-date-picker
                v-model="customerReturnOrder.completed_at"
                type="date"
                :disabled="customerReturnOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20"
          style="display: flex; align-items: center">
          <el-col :span="12">
            <el-form-item
              prop="comment"
              label="Megjegyzés"
            >
              <el-input
                v-model="customerReturnOrder.comment"
                type="textarea"
                :rows="2"
                style="word-break: normal;"
                :disabled="type === 'view'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label=""
              prop="is_billable"
              style="margin-bottom: 0"
            >
              <el-checkbox
                v-model="customerReturnOrder.is_billable"
                :disabled="type === 'view'"
              >
                Számlázandó
              </el-checkbox>
            </el-form-item>
            <el-form-item
              label=""
              prop="is_stockist"
              style="margin-bottom: 0"
            >
              <el-checkbox
                v-model="customerReturnOrder.is_stockist"
                :disabled="type === 'view'"
              >
                Beszerzési árak véglegesek
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :is-product-select-from-stock="false"
          :currency="customerReturnOrder.currency && customerReturnOrder.currency.code"
          :default-items="customerReturnOrder.order_items"
          :enabled-elements="enabledElements"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDate', 'count', 'unitPrice', 'purchaseUnitPrice', 'netPrice', 'vat', 'supplierSelector'] : ['product', 'productPackType', 'expirationDate', 'count', 'unitPrice', 'purchaseUnitPrice', 'netPrice', 'vat', 'actions', 'supplierSelector']"
          compare-type="completion"
          :compare-items="customerReturnOrder.inward_handling && customerReturnOrder.inward_handling.inward_handling_items"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ customerReturnOrder.net_price | currency(customerReturnOrder.currency.code) }}</strong>
        </div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ customerReturnOrder.gross_price | currency(customerReturnOrder.currency.code) }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="customerReturnOrder.status !== 'recorded' && customerReturnOrder.status !== 'cancelled' && customerReturnOrder.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import ProductItems from '../../utils/ProductItems/index';

  export default {
    components: {
      ProductItems,
    },
    props: ['type', 'customerReturnOrderId'],
    data() {
      return {
        loading: false,
        addressesLoading: false,
        receiptPrefix: '',
        buttonLoading: false,
        partnerLoading: false,
        cancelButtonLoading: false,
        restoreButtonLoading: false,
        addresses: [],
        suppliers: [],
        customerReturnOrder: {
          status: 'recorded',
          net_price: 0,
          gross_price: 0,
          order_items: [],
          storage: {},
          target_storage: {},
          customer: {},
          supplier: null,
          completed_at: null,
          currency: {},
          comment: null,
          is_billable: true,
          is_stockist: false,
          cm_delivery_note_number: '',
        },
        rules: {
          customer: [
            {
              required: true,
              message: 'Vevő kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          supplier: [
            {
              required: true,
              message: 'Beszállító kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          storage: [
            {
              required: true,
              message: 'Raktár kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          target_storage: [
            {
              required: true,
              message: 'Raktár kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          completed_at: [
            {
              required: true,
              message: 'Teljesítés megadása kötelező',
              trigger: 'change'
            }
          ],
          currency: [
            {
              required: true,
              message: 'Deviza megadása kötelező',
              trigger: 'change'
            }
          ],
          cm_delivery_note_number: [
            {
              max: 255,
              message: 'A CM szállítólevél szám maximális hossza 255 karakter',
              trigger: 'blur'
            }
          ],
        },
      };
    },
    computed: {
      enabledElements() {
        if (this.type === 'view') {
          return [];
        }
        switch (this.customerReturnOrder.status) {
          case 'recorded':
            return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'purchaseUnitPrice', 'expirationDate', 'supplierSelector'];
          case 'sent':
            return [];
          case 'done':
            return this.customerReturnOrder.is_stockist ? ['unitPrice'] : ['unitPrice', 'purchaseUnitPrice'];
          default:
            return [];
        }
      },
      customers() {
        return this.$store.getters['ownCompany/ownCompanies'];
      },
      storages() {
        return this.$store.getters['storage/ownStorages'];
      },
      currencies() {
        return this.$store.getters['currency/currencies'];
      },
    },
    async mounted() {
      this.loading = true;
      const requests = [];
      if (!this.currencies || !this.currencies.length) {
        requests.push(this.$store.dispatch('currency/fetchAll'));
      }
      if (!this.storages || !this.storages.length) {
        requests.push(this.$store.dispatch('storage/fetchAll'));
      }
      if (!this.customers || !this.customers.length) {
        requests.push(this.$store.dispatch('ownCompany/fetchAll', ''));
      }
      await Promise.all(requests);
      this.receiptPrefix = (await axios.get('api/receipt-prefixes/customerReturnOrder')).full_prefix;
      if (this.customerReturnOrderId) {
        this.customerReturnOrder = await this.$store.dispatch('customerReturnOrder/fetch', this.customerReturnOrderId);
        this.customerReturnOrder.order_items = this.customerReturnOrder.order_items.map(item => {
          return {
            ...item,
            purchase_unit_price: item.purchase_unit_price === 0 ? item.product.purchase_price : item.purchase_unit_price
          };
        });
        this.suppliers.push(this.customerReturnOrder.supplier);
        this.addresses = this.customerReturnOrder.supplier.company.addresses.filter(a => a.pivot.type === 'delivery');
        if (this.type === 'add') {
          this.customerReturnOrder.status = 'recorded';
          this.customerReturnOrder.receipt_number = null;
          this.customerReturnOrder.order_items = this.customerReturnOrder.order_items.map(item => {
            delete item.id;
            return item;
          });
        }
      } else {
        this.customerReturnOrder.customer = this.customers[0];
        this.customerReturnOrder.currency = this.currencies[0];
        this.customerReturnOrder.storage = this.storages[0];
        this.customerReturnOrder.completed_at = moment()
        .add(3, 'days')
        .format('Y-MM-DD');
      }
      this.loading = false;

    },
    methods: {
      async searchPartners(query) {
        this.partnerLoading = true;
        if (!query) {
          this.suppliers = [];
        } else {
          const queryString = new URLSearchParams({
            name: query,
          });
          this.suppliers = await this.$store.dispatch('partner/search', '?' + queryString.toString());
        }
        this.partnerLoading = false;
      },
      async onSupplierChange() {
        this.addressesLoading = true;
        this.addresses = await axios.get(`api/addresses/company/${this.customerReturnOrder.supplier.company.id}/delivery`);
        if (this.addresses) {
          this.customerReturnOrder.supplier_address = this.addresses[0];
        }
        this.addressesLoading = false;
      },
      onPriceChange({ netPrice, grossPrice }) {
        this.customerReturnOrder.net_price = netPrice;
        this.customerReturnOrder.gross_price = grossPrice;
      },
      handleClose() {
        if (this.type === 'view') {
          return this.$router.replace('/sales/customer-return-orders');
        }
        this.$confirm('Biztosan elveted a módosításokat?', {
          confirmButtonText: 'Igen',
          type: 'warning',
        })
        .then(() => {
          this.$router.replace('/sales/customer-return-orders');
        })
        .catch(() => {
        });
      },
      async cancel() {
        this.cancelButtonLoading = true;
        await this.$store.dispatch('customerReturnOrder/cancel', this.customerReturnOrder.id);
        this.loading = true;
        await this.$store.dispatch('customerReturnOrder/fetchAll');
        this.customerReturnOrder = await this.$store.dispatch('customerReturnOrder/fetch', this.customerReturnOrderId);
        this.loading = false;
        this.cancelButtonLoading = false;
      },
      async restore() {
        this.restoreButtonLoading = true;
        await this.$store.dispatch('customerReturnOrder/undoCancel', this.customerReturnOrder.id);
        this.loading = true;
        await this.$store.dispatch('customerReturnOrder/fetchAll');
        this.customerReturnOrder = await this.$store.dispatch('customerReturnOrder/fetch', this.customerReturnOrderId);
        this.loading = false;
        this.restoreButtonLoading = false;
      },
      async store() {
        this.$refs.orderForm.validate(async valid => {
          const expValid = this.$refs['productItems'].validateExpirationDates();
          const supplierValid = this.$refs['productItems'].validateSuppliers();
          if (!expValid || !supplierValid || !valid) {
            this.$messageWrapper.warning('Hibásan kitöltött form.');
            return;
          }
          if (valid) {
            this.customerReturnOrder.order_items = this.$refs['productItems'].getItems();
            if (!this.customerReturnOrder.order_items.length) {
              return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
            } else if (!this.$refs['productItems'].validateProducts()) {
              return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
            }
            this.buttonLoading = true;
            try {
              if (this.type === 'add') {
                await this.$store.dispatch('customerReturnOrder/create', this.customerReturnOrder);
              } else if (this.type === 'update') {
                await this.$store.dispatch('customerReturnOrder/update', this.customerReturnOrder);
              }
              this.$store.dispatch('customerReturnOrder/fetchAll');
              this.buttonLoading = false;
              this.$router.replace('/sales/customer-return-orders');
            } catch (e) {
            }
            this.buttonLoading = false;
          }
        });
      },
      openReceipt() {
        if (this.customerReturnOrder.receipt_file && this.customerReturnOrder.receipt_file.path) {
          window.open(this.customerReturnOrder.receipt_file.path);
        }
      },
      getStatus() {
        switch (this.customerReturnOrder.status) {
          case 'recorded':
            return {
              label: 'Rögzítve',
              tag: 'info',
            };
          case 'done':
            return {
              label: 'Visszavéve',
              tag: 'success',
            };
        }
      },
    },
  };
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;

    > div {
      display: inline-block;
    }
  }
</style>
